/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Card, CardContent, Typography, Grid, Box, Tabs, Tab, CircularProgress } from '@mui/material';
import DataTable from '../../../modules/dataTable/DataTable/DataTable';
import { columns, alarmHistoryColumns } from '../constants/columns';
import { RuntimeMetricsAutoTreatContext } from '../contexts/RuntimeMetricsAutoTreatContext';
import { MachinesContext } from '../../../modules/machinesAdminManagement/contexts/MachinesContext';
import RuntimeMetricAutoTreatFilterBar from '../components/RuntimeMetricsAutoTreatFilterBar';
import DetailDrawer from '../../../modules/navigation/components/DetailDrawer';
import RuntimeMetricSidebarContent from '../components/RuntimeMetricSidebarContent';
import TabPanel from '../../../modules/shared/tabs/TabPanel';

const RuntimeMetricsAutoTreatList: React.FunctionComponent = () => {
  const {
    fetchRuntimeMetricsAutoTreat,
    runtimeMetricsAutoTreat,
    runtimeMetricsAutoTreatLoading,
    activeMachine,
    fetchAlarmHistory,
    alarmHistory,
    alarmHistoryLoading,
  } = React.useContext(RuntimeMetricsAutoTreatContext);

  const [initialDataLoaded, setDataLoaded] = React.useState(false);
  const [tableFilters, setTableFilters] = React.useState({
    orderName: '',
    orderOperator: '',
    orderVariety: '',
    orderRecipe: '',
  });

  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedRuntimeMetric, setSelectedRuntimeMetric] = React.useState<any>(null);

  const [fitlerBarActive, setFitlerBarActive] = React.useState(true);
  const [tabValue, setTabValue] = React.useState(0);
  const { fetchAutoTreatMachines, autoTreatMachines, machinesLoading } = React.useContext(MachinesContext);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  React.useEffect(() => {
    const getMachines = async () => {
      if (!initialDataLoaded) {
        setDataLoaded(true);
        fetchAutoTreatMachines();
      }
    };

    getMachines();
  }, [initialDataLoaded, setDataLoaded, fetchRuntimeMetricsAutoTreat]);

  React.useEffect(() => {
    // Fetch alarm history when a machine is selected
    if (activeMachine?.value && activeMachine?.value !== 'Select A Machine') {
      fetchAlarmHistory(activeMachine.value);
    }
  }, [activeMachine?.value]);

  const handleSelectRuntimeMetric = (runtimeMetric: any) => {
    setSelectedRuntimeMetric(runtimeMetric);
    setDrawerOpen(true);
  };

  const filterRuntimeMetrics = () => {
    if (
      tableFilters.orderName ||
      tableFilters.orderOperator ||
      tableFilters.orderVariety ||
      tableFilters.orderRecipe
    ) {
      const orderNameFilteredRuntimeMetrics = runtimeMetricsAutoTreat.filter((order) =>
        order.OrderName?.toLowerCase().includes(tableFilters.orderName?.toLowerCase()),
      );

      const orderOperatorFilteredRuntimeMetrics = orderNameFilteredRuntimeMetrics.filter((order) =>
        order.OperatorName?.toLowerCase().includes(tableFilters.orderOperator?.toLowerCase()),
      );

      const orderVarietyFilteredRuntimeMetrics = orderOperatorFilteredRuntimeMetrics.filter((order) =>
        order.Variety?.toLowerCase().includes(tableFilters.orderVariety?.toLowerCase()),
      );

      // const orderRecipeFilteredRuntimeMetrics = orderVarietyFilteredRuntimeMetrics.filter((order) =>
      //   order.Recipe?.toLowerCase().includes(tableFilters.orderRecipe?.toLowerCase()),
      // );

      return orderVarietyFilteredRuntimeMetrics;
    }

    return runtimeMetricsAutoTreat;
  };

  return (
    <>
      <Grid justifyContent="space-between" container spacing={3}>
        <Grid item>
          <Typography variant="h1" component="h1">
            Runtime Metrics
          </Typography>
          <br />
        </Grid>
      </Grid>
      <br />
      {fitlerBarActive && (
        <Grid justifyContent="space-between" container>
          <Grid item md={12}>
            <RuntimeMetricAutoTreatFilterBar
              machineChoices={autoTreatMachines}
              machineChoicesLoading={machinesLoading}
              tableFilters={tableFilters}
              setTableFilters={setTableFilters}
            />
            <br />
          </Grid>
        </Grid>
      )}
      <br />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="runtime metrics tabs">
          <Tab style={{ textTransform: 'capitalize' }} label="Order Metrics" />
          <Tab style={{ textTransform: 'capitalize' }} label="Alarm Metrics" />
        </Tabs>
      </Box>
      <br />
      <DetailDrawer
        open={isDrawerOpen}
        recordLoading={false}
        onClose={() => setDrawerOpen(false)}
        bodyComponent={<RuntimeMetricSidebarContent runtimeMetric={selectedRuntimeMetric} />}
      />
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            {activeMachine && activeMachine?.value !== 'Select A Machine' ? (
              runtimeMetricsAutoTreatLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataTable
                  noHeader
                  columns={columns}
                  data={filterRuntimeMetrics()}
                  defaultSortField="OrderStartTime"
                  defaultSortAsc={false}
                  striped
                  highlightOnHover
                  pointerOnHover
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 25, 50]}
                  onRowClicked={(row) => {
                    handleSelectRuntimeMetric(row);
                  }}
                />
              )
            ) : (
              <Grid container alignItems="center" justifyContent="center">
                <Typography variant="h2" component="h2" style={{ marginTop: 15 }}>
                  Please select a machine to view details.
                </Typography>
              </Grid>
            )}
          </CardContent>
          <br />
        </Card>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            {activeMachine && activeMachine?.value !== 'Select A Machine' ? (
              alarmHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataTable
                  noHeader
                  columns={alarmHistoryColumns}
                  data={alarmHistory || []}
                  defaultSortField="log_time"
                  defaultSortAsc={false}
                  striped
                  highlightOnHover
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                />
              )
            ) : (
              <Grid container alignItems="center" justifyContent="center">
                <Typography variant="h2" component="h2" style={{ marginTop: 15 }}>
                  Please select a machine to view details.
                </Typography>
              </Grid>
            )}
          </CardContent>
          <br />
        </Card>
      </TabPanel>
    </>
  );
};

export default RuntimeMetricsAutoTreatList;
